import { ChevronDown, ChevronRight, Eye, EyeOff, Filter, Info, RefreshCw } from 'lucide-react';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import leetcodeSolvedQuestions from '../../assets/leetcode_solved_questions.png';
import { GlobalContext } from '../../contexts/GlobalContext';
import ExternalLink from '../common/ExternalLink';
import TutorialTooltip from '../DailyDigest/components/TutorialTooltip';
import SearchInput from '../SearchInput';
import TutorialRestartButton from '../TutorialRestartButton';
import { TUTORIAL_STEPS } from './config/tutorialConfig';
import ProgressBar from './ProgressBar';

const LeetcodeTracker = () => {
  const navigate = useNavigate();
  const {
    userProfile,
    updateTutorialFlag,
    questions,
    setQuestions,
    fetchQuestions,
    saveQuestionsToLocalStorage,
    loading,
    trialStatus,
    trialLoading,
  } = useContext(GlobalContext);
  // const [questions, setQuestions] = useState([]);
  const [showCategories, setShowCategories] = useState(true);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const selectedQuestionRef = useRef(null);
  const [difficulty, setDifficulty] = useState('all');
  const [isDifficultyDropdownOpen, setIsDifficultyDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState([]);
  const [useListView, setUseListView] = useState(false);
  const categories = [
    'Arrays & Hashing',
    'Two Pointers',
    'Sliding Window',
    'Stack',
    'Binary Search',
    'Intervals',
    'Linked List',
    'Trees',
    'Heap / Priority Queue',
    'Backtracking',
    'Tries',
    'Graphs',
    'Advanced Graphs',
    '1-D Dynamic Programming',
    '2-D Dynamic Programming',
    'Greedy',
    'Math & Geometry',
    'Bit Manipulation',
  ];
  const [showTutorial, setShowTutorial] = useState(false);

  useEffect(() => {
    if (userProfile && userProfile.onboarding_progress) {
      setShowTutorial(userProfile.onboarding_progress.hasSeenLeetcodeTutorial !== true);
    }
  }, [userProfile]);
  const [currentTutorialStep, setCurrentTutorialStep] = useState(0);
  const [tutorialAction, setTutorialAction] = useState(null);

  const [solvedPaste, setSolvedPaste] = useState('');
  const [isSyncDropdownOpen, setIsSyncDropdownOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false); // tooltip for image in sync modal
  const [syncedQuestions, setSyncedQuestions] = useState([]);
  const [showResetModal, setShowResetModal] = useState(false);

  // Add refs for tutorial nodes
  const nodeRefs = {
    'categories-overview': useRef(null),
    'first-category': useRef(null),
    'first-question': useRef(null),
    'mock-interview': useRef(null),
  };

  // Add effect to handle body scroll during tutorial
  useEffect(() => {
    console.log('Tutorial visibility changed:', showTutorial);
    if (showTutorial) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showTutorial]);

  const handleNextStep = () => {
    console.log('handleNextStep called');
    const currentStep = TUTORIAL_STEPS[currentTutorialStep];

    // If the current step has an action, set it
    if (currentStep.action === 'click') {
      setTutorialAction(currentStep.id);
    } else {
      setCurrentTutorialStep((prev) => prev + 1);
    }
  };

  const handleTutorialComplete = () => {
    setShowTutorial(false);
    updateTutorialFlag('hasSeenLeetcodeTutorial', true);
  };

  // Handle tutorial actions
  useEffect(() => {
    if (tutorialAction === 'first-category') {
      // Expand the Arrays & Hashing category
      if (!expandedCategories.includes('Arrays & Hashing')) {
        setExpandedCategories((prev) => [...prev, 'Arrays & Hashing']);
        // Wait longer for the category expansion animation and DOM update
        setTimeout(() => {
          setTutorialAction(null);
          // Add a small delay before moving to the next step
          setTimeout(() => {
            setCurrentTutorialStep((prev) => prev + 1);
          }, 50);
        }, 100);
      }
    } else if (tutorialAction === 'mock-interview' && questions.length > 0) {
      // Find the first Arrays & Hashing question
      const firstQuestion = questions.find((q) => q.category === 'Arrays & Hashing');
      if (firstQuestion) {
        startMockInterview(firstQuestion.question);
      }
      setTutorialAction(null);
      handleTutorialComplete();
    }
  }, [tutorialAction, expandedCategories, questions]);

  // Add this new effect to ensure tutorial state is maintained
  useEffect(() => {
    if (showTutorial && currentTutorialStep > 1) {
      // If we're past the first category step, ensure Arrays & Hashing is expanded
      if (!expandedCategories.includes('Arrays & Hashing')) {
        setExpandedCategories((prev) => [...prev, 'Arrays & Hashing']);
      }
    }
  }, [showTutorial, currentTutorialStep]);

  const canUseAIInterviews = trialStatus ? trialStatus.can_use_ai_interviews : true;

  const TutorialOverlay = () => {
    console.log('Rendering TutorialOverlay, showTutorial:', showTutorial);
    console.log('Current step:', currentTutorialStep);
    console.log('Current step data:', TUTORIAL_STEPS[currentTutorialStep]);
    if (!showTutorial) return null;

    const currentStep = TUTORIAL_STEPS[currentTutorialStep];
    const currentRef = nodeRefs[currentStep.id];
    console.log('Current ref:', currentRef?.current);
    const rect = currentRef?.current?.getBoundingClientRect();
    console.log('Current rect:', rect);

    if (!rect) {
      console.log('No rect found for step:', currentStep.id);
      return null;
    }

    const getTooltipPosition = () => {
      const windowWidth = window.innerWidth;
      const isSmallScreen = windowWidth < 1024;

      if (currentStep.id === 'categories-overview') {
        return {
          top: rect.top + 100, // Position in the middle of the visible table
          left: rect.right + 20,
          transform: 'none',
          width: '300px',
          '--arrow-orientation': 'left',
        };
      }

      if (currentStep.id === 'first-category') {
        return {
          top: rect.bottom + 20,
          left: rect.left + rect.width / 2 - 150,
          transform: 'none',
          width: '300px',
          '--arrow-orientation': 'up',
        };
      }

      if (currentStep.id === 'first-question') {
        return {
          top: rect.bottom + 20,
          left: rect.left + rect.width / 2 - 150,
          transform: 'none',
          width: '300px',
          '--arrow-orientation': 'up',
        };
      }

      if (currentStep.id === 'mock-interview') {
        return {
          top: rect.top + rect.height / 2 - 100,
          left: rect.left - 320,
          transform: 'none',
          width: '300px',
          '--arrow-orientation': 'right',
        };
      }

      if (isSmallScreen) {
        return {
          top: rect.bottom + 20,
          left: rect.left,
          transform: 'none',
          width: Math.min(300, rect.width) + 'px',
        };
      }

      return {
        top: rect.top + rect.height / 2 - 100,
        left: currentStep.placement === 'left' ? rect.left - 320 : rect.right + 20,
        width: '300px',
      };
    };

    return (
      <>
        <div className="fixed inset-0 z-50">
          {rect && (
            <div
              className="absolute bg-transparent"
              style={{
                top: currentStep.id === 'categories-overview' ? rect.top - 48 : rect.top,
                left: rect.left,
                width: rect.width,
                height:
                  currentStep.id === 'categories-overview' ? 'calc(100vh - 200px)' : rect.height,
                boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.7)',
                borderRadius: '0.5rem',
                pointerEvents: 'none', // Allow clicking through the overlay
              }}
            />
          )}
        </div>

        <TutorialTooltip
          step={currentStep}
          steps={TUTORIAL_STEPS}
          onNext={handleNextStep}
          onComplete={handleTutorialComplete}
          style={getTooltipPosition()}
        />
      </>
    );
  };

  useEffect(() => {
    fetchQuestions('');
  }, [fetchQuestions]);

  useEffect(() => {
    // When syncedQuestions changes, update the status of questions
    if (syncedQuestions.length > 0) {
      setQuestions((prevQuestions) => {
        const updatedQuestions = prevQuestions.map((question) => {
          if (syncedQuestions.includes(question.question.toLowerCase())) {
            return { ...question, status: 'solved' };
          }
          return question;
        });
        saveQuestionsToLocalStorage(updatedQuestions);
        return updatedQuestions;
      });
    }
  }, [syncedQuestions]);

  useEffect(() => {
    // Update state with values from local storage
    const savedDifficulty = localStorage.getItem('difficultyFilter');
    if (savedDifficulty !== null) {
      setDifficulty(JSON.parse(savedDifficulty));
    }

    const savedShowCategories = localStorage.getItem('showCategories');
    if (savedShowCategories !== null) {
      setShowCategories(JSON.parse(savedShowCategories));
    }

    const savedUseListView = localStorage.getItem('useListView');
    if (savedUseListView !== null) {
      setUseListView(JSON.parse(savedUseListView));
    }

    const savedExpandedCategories = localStorage.getItem('expandedCategories');
    if (savedExpandedCategories !== null) {
      setExpandedCategories(JSON.parse(savedExpandedCategories));
    }
  }, []);

  useEffect(() => {
    // Initialize tutorial when questions are loaded
    if (!loading && questions.length > 0 && showTutorial) {
      console.log('Questions loaded, initializing tutorial');
      // Ensure we're in grouped view for the tutorial
      setUseListView(false);
      // Reset any expanded categories
      setExpandedCategories([]);
      // Force a re-render of the tutorial overlay
      setCurrentTutorialStep(0);
    }
  }, [loading, questions, showTutorial]);

  const toggleDropdown = (name) => {
    setQuestions((prevQuestions) =>
      prevQuestions.map((question) =>
        question.question === name
          ? { ...question, isOpen: !question.isOpen }
          : { ...question, isOpen: false }
      )
    );
  };

  const updateStatus = (event, name, newStatus) => {
    event.stopPropagation();
    setQuestions((prevQuestions) => {
      const newQuestions = prevQuestions.map((question) =>
        question.question === name ? { ...question, status: newStatus, isOpen: false } : question
      );

      saveQuestionsToLocalStorage(newQuestions);
      return newQuestions;
    });
  };

  const toggleCategoriesVisibility = () => {
    setShowCategories((prev) => {
      const newShowCategories = !prev;
      localStorage.setItem('showCategories', JSON.stringify(newShowCategories));
      return newShowCategories;
    });
  };

  const toggleUseGroupedView = () => {
    setUseListView((prev) => {
      const newUseListView = !prev;
      localStorage.setItem('useListView', JSON.stringify(newUseListView));
      return newUseListView;
    });
  };

  const toggleCategory = (category) => {
    setExpandedCategories((prev) => {
      const newExpandedCategories = prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category];
      localStorage.setItem('expandedCategories', JSON.stringify(newExpandedCategories));
      return newExpandedCategories;
    });
  };

  // useEffect(() => {
  //   const handleClickOutside = (event) => {
  //     if (!event.target.closest(".dropdown-button")) {
  //       setQuestions((prevQuestions) =>
  //         prevQuestions.map((question) => ({
  //           ...question,
  //           isOpen: false,
  //         }))
  //       );
  //     }
  //   };

  //   document.addEventListener("mousedown", handleClickOutside);
  //   return () => document.removeEventListener("mousedown", handleClickOutside);
  // }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'solved':
        return 'bg-green-200 text-green-800 hover:bg-green-200';
      case 'attempted':
        return 'bg-yellow-200 text-yellow-800 hover:bg-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 hover:bg-gray-200';
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty.toLowerCase()) {
      case 'easy':
        return 'bg-green-200 text-green-800';
      case 'medium':
        return 'bg-yellow-200 text-yellow-800';
      case 'hard':
        return 'bg-red-200 text-red-800';
      default:
        return 'bg-gray-200 text-gray-800';
    }
  };

  const filteredQuestions = useMemo(() => {
    return questions.filter((question) => {
      const matchesSearch = question.question.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesDifficulty =
        difficulty === 'all' || question.difficulty.toLowerCase() === difficulty;
      return matchesSearch && matchesDifficulty;
    });
  }, [questions, searchQuery, difficulty]);

  const filteredProgress = useMemo(() => {
    const solved = filteredQuestions.filter((q) => q.status === 'solved').length;
    const total = filteredQuestions.length;
    return { solved, total };
  }, [filteredQuestions]);

  const handleDifficultyChange = (newDifficulty) => {
    setDifficulty(newDifficulty);
    setIsDifficultyDropdownOpen(false);
    localStorage.setItem('difficultyFilter', JSON.stringify(newDifficulty));
  };

  const startMockInterview = (problemName) => {
    if (!canUseAIInterviews) {
      return; // Don't navigate if access is restricted
    }
    const encodedProblemName = encodeURIComponent(problemName);
    navigate(`/technical/mock-interview/${encodedProblemName}`);
  };

  const handleSync = () => {
    setIsSyncDropdownOpen(false);
    setSolvedPaste('');
    setSyncedQuestions(parseLeetcodePaste(solvedPaste));
  };

  const resetProgress = () => {
    const updatedQuestions = questions.map((question) => ({
      ...question,
      status: 'unsolved',
    }));
    setQuestions(updatedQuestions);
    saveQuestionsToLocalStorage(updatedQuestions);
  };

  const parseLeetcodePaste = (solvedPaste) => {
    try {
      const pattern = /\d+\.\s(.+)\n/g;
      const matches = [];
      let match;

      const input = solvedPaste + '\n';

      // Find all matches
      while ((match = pattern.exec(input)) !== null) {
        matches.push(match[1]);
      }

      // Convert all titles to lowercase
      return matches.map((title) => title.toLowerCase());
    } catch (e) {
      console.error(e);
      return [];
    }
  };

  const renderInterviewButton = (question, category = null, index = null) => {
    if (trialLoading) {
      return (
        <button
          disabled
          className="inline-flex items-center gap-x-2 text-sm font-semibold rounded-lg border border-transparent bg-gray-400 text-white px-4 py-2 truncate cursor-not-allowed"
        >
          Loading...
        </button>
      );
    }

    if (!canUseAIInterviews) {
      return (
        <div className="text-center">
          <button
            disabled
            className="inline-flex items-center gap-x-2 text-sm font-semibold rounded-lg border border-gray-300 bg-gray-100 text-gray-500 px-4 py-2 truncate cursor-not-allowed mb-1"
            title="Upgrade required"
          >
            🔒 Start Interview
          </button>
          <div className="text-xs text-orange-600 font-medium">Disabled on Basic Trial</div>
        </div>
      );
    }

    const shouldHaveRef = category === 'Arrays & Hashing' && index === 0;

    return (
      <button
        onClick={() => startMockInterview(question.question)}
        className="inline-flex items-center gap-x-2 text-sm font-semibold rounded-lg border border-transparent bg-blue-600 text-white hover:bg-blue-700 px-4 py-2 truncate"
        ref={shouldHaveRef ? nodeRefs['mock-interview'] : null}
      >
        Start Interview
      </button>
    );
  };

  return (
    <div className="flex-1 px-3 flex flex-col items-center">
      <div className="px-4 sm:px-6 lg:px-8 lg:pb-7 mx-auto max-w-340 w-full">
        <div className="flex flex-col">
          <div className="p-1.5 min-w-full inline-block align-middle">
            <div className="bg-white border border-gray-200 rounded-xl shadow-xs overflow-hidden">
              {/* Header */}
              <div className="px-6 py-4 grid gap-3 md:flex md:justify-between md:items-center border-b border-gray-200">
                <SearchInput searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
                <div>
                  <button
                    onClick={toggleUseGroupedView}
                    className="inline-flex items-center gap-x-2 p-2 text-sm font-medium rounded-lg border border-gray-200 bg-white text-gray-800 shadow-xs hover:bg-gray-50"
                  >
                    {useListView ? 'Grouped View' : 'List View'}
                  </button>
                </div>
                <div className="relative inline-block">
                  <button
                    type="button"
                    className="flex items-center py-2 px-4 text-sm font-medium rounded-lg border border-gray-200 bg-white text-gray-800 shadow-xs hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none"
                    data-hs-overlay="#leetcode-modal"
                    onClick={() => setIsSyncDropdownOpen(true)}
                    data-hs-tooltip="Sync with LeetCode"
                  >
                    <RefreshCw className="h-4 w-4 mr-2 min-h-4 min-w-4" />
                    Sync with Leetcode
                  </button>
                  <div
                    className="hs-tooltip-content hs-tooltip-shown:opacity-100 hs-tooltip-shown:visible opacity-0 transition-opacity inline-block absolute invisible z-10 py-1 px-2 bg-gray-900 text-xs font-medium text-white rounded-md shadow-xs"
                    role="tooltip"
                  >
                    Sync with LeetCode
                  </div>
                </div>

                <div className="flex justify-center">
                  <div className="relative inline-block">
                    <button
                      type="button"
                      className="py-3 px-4 inline-flex items-center gap-x-2 text-sm font-medium rounded-lg border border-gray-200 bg-white text-gray-800 shadow-xs hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none"
                      onClick={() => setIsDifficultyDropdownOpen(!isDifficultyDropdownOpen)}
                    >
                      Difficulty
                      <Filter className="h-4 w-4" />
                      <span className="sr-only">Filter</span>
                      <svg
                        className={`w-4 h-4 transition-transform ${
                          isDifficultyDropdownOpen ? 'rotate-180' : ''
                        }`}
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="m6 9 6 6 6-6" />
                      </svg>
                    </button>

                    {isDifficultyDropdownOpen && (
                      <div className="absolute z-10 mt-2 rounded-lg bg-white shadow-lg ring-1 ring-black/5 left-1/2 transform -translate-x-1/2">
                        <div className="py-1">
                          <button
                            className="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            onClick={() => handleDifficultyChange('all')}
                          >
                            All
                          </button>
                          <button
                            className="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            onClick={() => handleDifficultyChange('easy')}
                          >
                            Easy
                          </button>
                          <button
                            className="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            onClick={() => handleDifficultyChange('medium')}
                          >
                            Medium
                          </button>
                          <button
                            className="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            onClick={() => handleDifficultyChange('hard')}
                          >
                            Hard
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <ProgressBar
                solved={filteredProgress.solved}
                total={filteredProgress.total}
                difficulty={difficulty}
                setShowResetModal={setShowResetModal}
              />
              <table className="min-w-full divide-y divide-gray-200 table-fixed">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="w-1/6 px-6 py-3 text-start"
                      ref={nodeRefs['categories-overview']}
                    >
                      <div className="flex items-center gap-x-2 overflow-hidden">
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 truncate">
                          Status
                        </span>
                      </div>
                    </th>
                    <th scope="col" className="w-1/3 px-6 py-3 text-start">
                      <div className="flex items-center gap-x-2 overflow-hidden">
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 truncate">
                          Name
                        </span>
                      </div>
                    </th>
                    <th scope="col" className="w-1/6 px-6 py-3 text-start">
                      <div className="flex items-center gap-x-2 overflow-hidden">
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 truncate">
                          Category
                        </span>
                        <button
                          className="p-1 inline-flex items-center gap-x-2 text-sm font-medium rounded-lg hover:bg-gray-100 shrink-0"
                          onClick={toggleCategoriesVisibility}
                        >
                          {showCategories ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </th>
                    <th scope="col" className="w-1/6 px-6 py-3 text-start">
                      <div className="flex items-center justify-center gap-x-2 overflow-hidden">
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 truncate">
                          Difficulty
                        </span>
                      </div>
                    </th>
                    <th scope="col" className="w-1/6 px-6 py-3 text-start">
                      <div className="flex items-center gap-x-2 overflow-hidden">
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 truncate">
                          Mock Interview
                        </span>
                      </div>
                    </th>
                  </tr>
                </thead>
                {useListView ? (
                  <tbody className="divide-y divide-gray-200">
                    {filteredQuestions.map((question, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="w-1/6 px-6 py-4">
                          <div className="relative inline-block">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleDropdown(question.question);
                              }}
                              className={`dropdown-button py-1.5 px-4 inline-flex items-center gap-x-2 text-sm font-medium rounded-lg ${getStatusColor(
                                question.status
                              )}`}
                            >
                              {question.status.charAt(0).toUpperCase() + question.status.slice(1)}
                              <ChevronDown
                                className={`w-4 h-4 transition-transform ${
                                  question.isOpen ? 'rotate-180' : ''
                                }`}
                              />
                            </button>

                            {question.isOpen && (
                              <div className="absolute z-10 mt-2 w-48 rounded-lg bg-white shadow-lg ring-1 ring-black/5">
                                <div className="py-1">
                                  <button
                                    className="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                    onClick={(e) => updateStatus(e, question.question, 'unsolved')}
                                  >
                                    Unsolved
                                  </button>
                                  <button
                                    className="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                    onClick={(e) => updateStatus(e, question.question, 'attempted')}
                                  >
                                    Attempted
                                  </button>
                                  <button
                                    className="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                    onClick={(e) => updateStatus(e, question.question, 'solved')}
                                  >
                                    Solved
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="w-1/3 px-6 py-4 overflow-hidden">
                          <ExternalLink
                            href={question.link}
                            className="text-blue-600 hover:underline wrap-break-word block truncate"
                          >
                            {question.question}
                          </ExternalLink>
                        </td>
                        <td className="w-1/6 px-6 py-4 overflow-hidden">
                          <div
                            className={`transition-opacity duration-300 ease-in-out truncate ${
                              showCategories ? 'opacity-100' : 'opacity-0'
                            }`}
                          >
                            {question.category}
                          </div>
                        </td>
                        <td className="w-1/6 px-6 py-4 overflow-hidden">
                          <div className="flex justify-center">
                            <span
                              className={`inline-flex items-center gap-x-1.5 py-1.5 px-3 rounded-md text-xs font-medium truncate ${getDifficultyColor(
                                question.difficulty
                              )}`}
                            >
                              {question.difficulty}
                            </span>
                          </div>
                        </td>
                        <td className="w-1/6 px-6 py-4 overflow-hidden">
                          {renderInterviewButton(question)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                ) : (
                  <tbody className="divide-y divide-gray-200">
                    {categories.map((category, categoryIndex) => {
                      const categoryQuestions = filteredQuestions.filter(
                        (q) => q.category === category
                      );
                      const solvedCount = categoryQuestions.filter(
                        (q) => q.status === 'solved'
                      ).length;
                      const isExpanded = expandedCategories.includes(category);

                      return (
                        <React.Fragment key={category}>
                          <tr
                            className="hover:bg-gray-50 cursor-pointer"
                            onClick={() => toggleCategory(category)}
                            ref={
                              category === 'Arrays & Hashing' ? nodeRefs['first-category'] : null
                            }
                          >
                            <td colSpan="5" className="px-6 py-4 overflow-hidden">
                              <div className="flex items-center">
                                {isExpanded ? (
                                  <ChevronDown className="mr-2 h-4 w-4 shrink-0" />
                                ) : (
                                  <ChevronRight className="mr-2 h-4 w-4 shrink-0" />
                                )}
                                <span className="font-medium truncate">{category}</span>
                                <span className="ml-2 text-gray-500 shrink-0">
                                  {solvedCount} / {categoryQuestions.length}
                                </span>
                              </div>
                            </td>
                          </tr>
                          {isExpanded &&
                            categoryQuestions.map((question, index) => (
                              <tr
                                key={`${category}-${index}`}
                                className="hover:bg-gray-50"
                                ref={
                                  category === 'Arrays & Hashing' && index === 0
                                    ? nodeRefs['first-question']
                                    : null
                                }
                              >
                                <td className="w-1/6 px-6 py-4">
                                  <div className="relative inline-block">
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleDropdown(question.question);
                                      }}
                                      className={`dropdown-button py-1.5 px-4 inline-flex items-center gap-x-2 text-sm font-medium rounded-lg ${getStatusColor(
                                        question.status
                                      )}`}
                                    >
                                      {question.status.charAt(0).toUpperCase() +
                                        question.status.slice(1)}
                                      <ChevronDown
                                        className={`w-4 h-4 transition-transform ${
                                          question.isOpen ? 'rotate-180' : ''
                                        }`}
                                      />
                                    </button>

                                    {question.isOpen && (
                                      <div className="absolute z-10 mt-2 w-48 rounded-lg bg-white shadow-lg ring-1 ring-black/5">
                                        <div className="py-1">
                                          <button
                                            className="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                            onClick={(e) =>
                                              updateStatus(e, question.question, 'unsolved')
                                            }
                                          >
                                            Unsolved
                                          </button>
                                          <button
                                            className="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                            onClick={(e) =>
                                              updateStatus(e, question.question, 'attempted')
                                            }
                                          >
                                            Attempted
                                          </button>
                                          <button
                                            className="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                            onClick={(e) =>
                                              updateStatus(e, question.question, 'solved')
                                            }
                                          >
                                            Solved
                                          </button>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </td>
                                <td className="w-1/3 px-6 py-4 overflow-hidden">
                                  <ExternalLink
                                    href={question.link}
                                    className="text-blue-600 hover:underline wrap-break-word block truncate"
                                  >
                                    {question.question}
                                  </ExternalLink>
                                </td>
                                <td className="w-1/6 px-6 py-4 overflow-hidden">
                                  <div
                                    className={`transition-opacity duration-300 ease-in-out truncate ${
                                      showCategories ? 'opacity-100' : 'opacity-0'
                                    }`}
                                  >
                                    {question.category}
                                  </div>
                                </td>
                                <td className="w-1/6 px-6 py-4 overflow-hidden">
                                  <div className="flex justify-center">
                                    <span
                                      className={`inline-flex items-center gap-x-1.5 py-1.5 px-3 rounded-md text-xs font-medium truncate ${getDifficultyColor(
                                        question.difficulty
                                      )}`}
                                    >
                                      {question.difficulty}
                                    </span>
                                  </div>
                                </td>
                                <td className="w-1/6 px-6 py-4 overflow-hidden">
                                  {renderInterviewButton(question, category, index)}
                                </td>
                              </tr>
                            ))}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                )}
              </table>
            </div>
          </div>
        </div>
      </div>
      {showTutorial && <TutorialOverlay />}
      <TutorialRestartButton tutorialKey="hasSeenLeetcodeTutorial" />
      {/* Modal */}
      {isSyncDropdownOpen && (
        <div className="fixed inset-0 z-40 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen mt-8 pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div
              className="fixed inset-0 bg-gray-500/75 transition-opacity"
              onClick={() => setIsSyncDropdownOpen(false)}
            ></div>

            {/* Modal panel */}
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all w-1/2">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Sync LeetCode Solved Questions
                    </h3>
                    <div className="mt-4 space-y-4">
                      <p>
                        Please go to Leetcode and copy your list of{' '}
                        <span
                          className="text-blue-600 hover:underline cursor-pointer inline-flex items-center relative"
                          onMouseEnter={() => setShowTooltip(true)}
                          onMouseLeave={() => setShowTooltip(false)}
                        >
                          solved questions <Info className="inline-block w-4 h-4 ml-1" />
                          {/* Simple tooltip that appears on hover */}
                          {showTooltip && (
                            <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 w-[800px] mt-10">
                              <div className="bg-white border-2 border-gray-300 p-4 rounded-md shadow-lg">
                                {/* Placeholder text box instead of image */}
                                <div className="w-full h-[400px] bg-gray-100 rounded-md flex items-center justify-center text-gray-500 text-sm">
                                  <img
                                    src={leetcodeSolvedQuestions || '/placeholder.svg'}
                                    alt="Example of LeetCode solved questions format"
                                    style={{
                                      width: '100%',
                                      height: 'auto',
                                      maxWidth: '800px',
                                    }}
                                    className="rounded-md"
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        </span>
                        <br />
                        Click{' '}
                        <ExternalLink
                          href="https://leetcode.com/problemset/"
                          className="text-blue-600 hover:underline"
                        >
                          here
                        </ExternalLink>
                        , sign in, filter by solved questions, and scroll to the bottom. Then copy
                        your solved questions and paste them below.
                      </p>
                      <textarea
                        placeholder={`Example format:\n1. Two Sum\n53.7%\nEasy\n\n2. Add Two Numbers\n44.1%\nMedium\n\n3. Longest Substring Without Repeating Characters\n35.4%\nMedium\n\n4. Median of Two Sorted Arrays\n41.4%\nHard\n\n5. Longest Palindromic Substring\n34.4%\nMedium`}
                        value={solvedPaste}
                        onChange={(e) => setSolvedPaste(e.target.value)}
                        rows={15}
                        className="w-full px-3 py-2 text-gray-700 border rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-xs px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={handleSync}
                >
                  Sync
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-xs px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setIsSyncDropdownOpen(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Reset Progress Modal */}
      {showResetModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-sm shadow-lg max-w-sm w-full">
            <h3 className="text-lg font-semibold mb-4">Reset Progress?</h3>
            <p className="mb-4">This will mark all questions as unsolved. Are you sure?</p>
            <div className="flex justify-end gap-2">
              <button
                className="px-4 py-2 bg-gray-200 rounded-sm hover:bg-gray-300"
                onClick={() => setShowResetModal(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-red-500 text-white rounded-sm hover:bg-red-600"
                onClick={() => {
                  resetProgress();
                  setShowResetModal(false);
                }}
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeetcodeTracker;
